import { useState } from 'react';
import { Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useEnterprisePartnerList } from '@/hooks/useEnterprisePartners';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PartnerForm } from './PartnerForm';
import { PartnerSecretRotation } from './PartnerSecretRotation';

interface PartnerRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  provisioning_mode: string;
  shared_workspace_id: string | null;
  default_member_role: string | null;
  portal_url: string | null;
  created_at: string;
  updated_at: string;
}

interface PartnerListProps {
  onSelect: (partnerId: string) => void;
}

function statusBadgeVariant(
  status: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active':
      return 'default';
    case 'suspended':
      return 'secondary';
    case 'deleted':
      return 'destructive';
    default:
      return 'outline';
  }
}

function humanizeMode(mode: string): string {
  if (mode === 'shared') return 'Shared';
  if (mode === 'per_user') return 'Per-user';
  return mode;
}

export function PartnerList({ onSelect }: PartnerListProps): JSX.Element {
  const [showForm, setShowForm] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const { data, isLoading, error } = useEnterprisePartnerList();

  const partners = (data ?? []) as PartnerRow[];

  const handleCreateSuccess = (secret: string) => {
    setNewSecret(secret);
  };

  const handleSecretClose = () => {
    setNewSecret(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Enterprise Partners</h3>
        <Button onClick={() => setShowForm(true)} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Create new
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-10 animate-pulse rounded bg-muted" />
          <div className="h-10 animate-pulse rounded bg-muted" />
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load enterprise partners:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {!isLoading && !error && partners.length === 0 && (
        <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
          No enterprises yet — click &ldquo;Create new&rdquo; to add one.
        </div>
      )}

      {!isLoading && !error && partners.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.map((partner) => (
              <TableRow
                key={partner.id}
                className="cursor-pointer"
                onClick={() => onSelect(partner.id)}
              >
                <TableCell className="font-medium">{partner.name}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {partner.slug}
                </TableCell>
                <TableCell>
                  <Badge variant={statusBadgeVariant(partner.status)}>
                    {partner.status}
                  </Badge>
                </TableCell>
                <TableCell>{humanizeMode(partner.provisioning_mode)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(partner.created_at), {
                    addSuffix: true,
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {showForm && newSecret === null && (
        <PartnerForm
          mode="create"
          onSuccess={handleCreateSuccess}
          onClose={() => setShowForm(false)}
        />
      )}

      {newSecret !== null && (
        <PartnerSecretRotation
          signingSecretPlaintext={newSecret}
          onAcknowledge={handleSecretClose}
        />
      )}
    </div>
  );
}
